import { Info, RotateCcw } from 'lucide-react'
import {
  CRITERIA_LABELS,
  SCORING_FIELD_KEYS,
  type ScoringFieldKey,
  type Weights,
} from '@ispot/shared'

interface Props {
  weights: Weights
  dirty: boolean
  onChange: (key: ScoringFieldKey, value: number) => void
  onCommit: () => void
  onReset: () => void
}

export function WeightsPanel({ weights, dirty, onChange, onCommit, onReset }: Props) {
  const total = SCORING_FIELD_KEYS.reduce((acc, k) => acc + (weights[k] ?? 0), 0)
  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h3
          className="text-[10px] font-semibold uppercase tracking-wider"
          style={{ color: 'var(--color-text-muted)' }}
        >
          Criterion weights
        </h3>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide"
          style={{ color: 'var(--color-text-muted)' }}
          title="Reset to defaults"
        >
          <RotateCcw className="h-3 w-3" /> Reset
        </button>
      </div>
      <div className="space-y-1.5">
        {SCORING_FIELD_KEYS.map((key) => {
          const v = weights[key] ?? 0
          return (
            <div key={key} className="grid grid-cols-[90px_1fr_34px] items-center gap-2">
              <span className="text-xs" style={{ color: 'var(--color-text)' }}>
                {CRITERIA_LABELS[key].label}
              </span>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={v}
                onChange={(e) => onChange(key, parseFloat(e.target.value))}
                onMouseUp={onCommit}
                onTouchEnd={onCommit}
                onKeyUp={onCommit}
                className="w-full accent-[var(--color-market)]"
                aria-label={`${CRITERIA_LABELS[key].label} weight`}
              />
              <span
                className="text-right text-[11px] tabular-nums"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                {(v * 100).toFixed(0)}%
              </span>
            </div>
          )
        })}
      </div>
      <p
        className="mt-2 flex items-center gap-1 text-[10px]"
        style={{ color: dirty ? 'var(--color-market)' : 'var(--color-text-muted)' }}
      >
        <Info className="h-2.5 w-2.5" />
        Weights sum {(total * 100).toFixed(0)}% · normalized on save
        {dirty ? ' — release to apply' : ''}
      </p>
    </section>
  )
}
